// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

// --- Services ---
import problemSvc from "../../services/api/problem";

// --- Models ---
import { ProblemItem, ProblemFilter } from "../../models/types/problem";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { useNavigate } from "react-router-dom";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";
import MenuItem from "@mui/material/MenuItem";
import ButtonMore from "../../components/button/buttonMore";
import ProblemFilterView from "./filter";

// --- Icons ---
import { Trash2, Pencil, Eye, ListMinus } from "lucide-react";

// --- Utils ---
import dayjs from "dayjs";
import { listProblemType } from "../../utils/helper";
import { useResponsive } from "../../hooks/mediaQuery";

// --- Lookup ---
import { useLookupStation } from "../../utils/get-lookup";
import { useTranslation } from "react-i18next";

export default function ProblemPage() {
    const navigate = useNavigate();
    const { lookupStation } = useLookupStation();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { isXs, isLg, isXl } = useResponsive();
    const { t } = useTranslation();

    const [data, setData] = useState<ProblemItem[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<ProblemItem | null>(null);

    const getListAll = async (params: DefaultFilterRequest<ProblemFilter>) => {
        setLoading(true);
        try {
            const res = await problemSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify("Lỗi", "Xảy ra lỗi", "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: ProblemItem) => {
        try {
            const res = await problemSvc.deleteProblem(row.id);
            if (!res.err) {
                notify("Thành công", "Xóa vấn đề thành công", "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify("Lỗi", "Xảy ra lỗi", "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const fromTo = queryParams.get("fromTo");
        const params: DefaultFilterRequest<ProblemFilter> = {
            sorting: queryParams.get("sorting") || "modifiedDate desc",
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            isDelete: queryParams.get("isDelete") || "false",
            status: queryParams.get("status"),
            stationId: queryParams.get("stationId"),
            problemType: queryParams.get("problemType"),
            start: fromTo ? fromTo.split(",")[0] : null,
            end: fromTo ? fromTo.split(",")[1] : null,
        };

        await getListAll(params);
    };
    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: ProblemItem) => {
        navigate(UrlCollection.EditProblem.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: ProblemItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const handleView = (row: ProblemItem) => {
        navigate(UrlCollection.ViewProblem.replace(":id", row.id.toString()));
    };

    const columns = [
        { header: t('problem.table.stt'), accessor: "index", sortable: false, className: "max-w-[50px]" },
        {
            header: t('problem.table.station'),
            accessor: (row: ProblemItem) => lookupStation.find((item) => item.value === row.stationId)?.label,
            sortable: false,
            sortBy: "stationId",
            className: "min-w-[150px]",
        },
        {
            header: t('problem.table.problem'),
            accessor: (row: ProblemItem) => listProblemType.find((item) => item.value === row.problemType)?.label,
            sortable: false,
            sortBy: "problemType",
            className: "min-w-[90px]",
        },
        { header: t('problem.table.description'), accessor: "description", sortable: false, hidden: isXs, className: "min-w-[150px]" },
        { header: t('problem.table.status'), accessor: "statusName", sortable: false },
        {
            header: t('problem.table.startDate'),
            accessor: (row: ProblemItem) => `${row.start ? dayjs(row.start).format("DD/MM/YYYY") : "-"}`,
            sortable: true,
            hidden: isXs,
        },
        {
            header: t('problem.table.endDate'),
            accessor: (row: ProblemItem) => `${row.end ? dayjs(row.end).format("DD/MM/YYYY") : "-"}`,
            sortable: true,
            hidden: isXs,
            className: "min-w-[85px]",
        },
        {
            header: "",
            accessor: (row: ProblemItem) => {
                if (isXl || isLg) {
                    return (
                        <div className="flex gap-2">
                            <ButtonAction
                                onClick={() => handleView(row)}
                                icon={<Eye size={14} />}
                                color="blue"
                                title={t('problem.action.view')}
                            />

                            <ButtonAction
                                onClick={() => handleEdit(row)}
                                icon={<Pencil size={14} />}
                                color="blue"
                                title={t('problem.action.edit')}
                            />

                            <ButtonAction
                                disabled={row.status !== 1}
                                onClick={() => handleDelete(row)}
                                icon={<Trash2 size={14} />}
                                color="red"
                                title={t('problem.action.delete')}
                            />
                        </div>
                    )
                } else {
                    return (
                        <div className="">
                            <ButtonMore
                                icon={<ListMinus size={14} />}
                                title="More"
                            >
                                <MenuItem onClick={() => handleView(row)}>
                                    <Eye size={14} color="blue" className="mr-2" />
                                    <span>{t('problem.action.view')}</span>
                                </MenuItem>
                                <MenuItem onClick={() => handleEdit(row)}>
                                    <Pencil size={14} color="blue" className="mr-2" />
                                    <span>{t('problem.action.edit')}</span>
                                </MenuItem>
                                {row.status === 1 && (
                                    <MenuItem onClick={() => handleDelete(row)}>
                                        <Trash2 size={14} color="red" className="mr-2" />
                                        <span>{t('problem.action.delete')}</span>
                                    </MenuItem>
                                )}
                            </ButtonMore>
                        </div>
                    )
                }
            },
            sortable: false,
            className: "max-w-[100px]",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header">
                <h1 className="text-xl font-semibold">{t('problem.title')}</h1>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateProblem)}
                >
                    {t('problem.addNew')}
                </button>
            </div>
            <div className={`flex ${isXs ? 'my-1' : 'my-2'}`}>
                <ProblemFilterView />
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title={t('problem.modal.confirmDelete.title')}
                    content={t('problem.modal.confirmDelete.content')}
                    titleComfirm={t('problem.modal.confirmDelete.confirm')}
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
        </div>
    );
}
